import type { SessionUser } from "./auth-context";

/**
 * Permission strings the backoffice gates on. They come from the API's
 * permission catalog (`GET /api/auth/me` -> `permissions`); the UI never maps
 * roles to abilities itself. The API remains the source of truth: this only
 * decides what to render.
 */
export const Permission = {
  /** Read memberships (user detail) and the invitations view. ADMIN + SUPPORT. */
  ORG_MEMBERS_READ: "org.members.read",
  /** Write memberships / staff assignments from the backoffice. ADMIN. */
  ORG_MEMBERS_MANAGE: "org.members.manage",
  /** Move a member between STAFF and ADMIN (role change in place). ADMIN. */
  ORG_ADMINS_MANAGE: "org.admins.manage",
  /** Rename an organization from the backoffice. ADMIN. */
  ORG_NAME_EDIT: "org.name.edit",
  /** Record / revoke offline ToS acceptances and the ToS catalog. ADMIN. */
  TOS_OFFLINE_MANAGE: "tos.offline.manage",
} as const;

export type Permission = (typeof Permission)[keyof typeof Permission];

/**
 * Whether the session holds `permission` GLOBALLY (the backoffice's internal
 * accounts; the API emits no permissions for other account types). It is not
 * resource-scoped: ADR-0017's organization-scoped checks (`can(permission,
 * { organizationId })`, for the Partners app) need a different helper and
 * must not reuse this one. No user (or an older API that sends
 * no permissions) means no permission: the check fails closed.
 */
export function hasPermission(
  user: Pick<SessionUser, "permissions"> | null | undefined,
  permission: Permission,
): boolean {
  return user?.permissions.includes(permission) ?? false;
}
